<script lang="ts" setup>
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import type FakturacneUdaje from '@/types/FakturacneUdaje'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyMemberDataService from '@/services/CompanyMemberDataService'
import type CompanyMember from '@/types/CompanyMember'
import { onMounted, ref, computed } from 'vue';
import store from '@/store'
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const currentOrder = computed(() => store.state.currentOrder);

const currentOrderAddress = ref({} as Address);
const currentCompanyAddress = ref({} as Address);
const currentUserAddress = ref({} as Address);
const currentFakturacneUdaje = ref({} as FakturacneUdaje);
const currentUser = ref({} as User);
const currentCompany = ref({} as Company);
const currentHeadquarters = ref({} as Headquarters);
const currentCompanyMembers = ref([] as CompanyMember[]);
// (Types already imported above)
// These types are not defined in the codebase, so we use minimal inline types for now
type OrderItem = { description: string; price?: string; price_vat?: string;[key: string]: any };
type SubStatus = { id: number; description: string };
type HeadquartersType = { id: number; description: string };
type SposobKonania = { id: number; description: string };
type Kuv = { id: number; description: string };

const items = ref<OrderItem[]>([]);
const predmetyPodnikania = ref<OrderItem[]>([]);
const tab = ref(1);
const companySubStatuses = ref<SubStatus[]>([]);
const headquartersTypes = ref<HeadquartersType[]>([]);
const sposobyKonaniaKonatelov = ref<SposobKonania[]>([]);
const konecnyUzivateliaVyhod = ref<Kuv[]>([]);
const isLoaded = ref(false);

function currentTab(tabNumber: number) {
    tab.value = tabNumber
}

async function getUser(id: number) {
    await UserDataService.getUserById(id)
        .then((response: ResponseData) => {
            currentUser.value = response.data.data
            if (currentUser.value.address_id > 0) {
                getUserAddress(currentUser.value.address_id)
            }
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: number) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
            predmetyPodnikania.value = JSON.parse(currentCompany.value.subjects_of_business)
            getCompanyHeadquarters(currentCompany.value.headquarters_id)
            getCompanyMembers(currentCompany.value.id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyMembers(id: number) {
    await CompanyMemberDataService.getAllCompanyMembersForCompany(id)
        .then((response: ResponseData) => {
            currentCompanyMembers.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getOrderAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then((response: ResponseData) => {
                currentOrderAddress.value = response.data.data
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function getUserAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then((response: ResponseData) => {
                currentUserAddress.value = response.data.data
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function getCompanyAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then((response: ResponseData) => {
                currentCompanyAddress.value = response.data.data
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function getCompanyHeadquarters(id: number) {
    await HeadquartersDataService.getHeadquartersById(id)
        .then((response: ResponseData) => {
            currentHeadquarters.value = response.data.data
            if (currentHeadquarters.value.address_id > 0) {
                getCompanyAddress(currentHeadquarters.value.address_id)
            }
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getFakturacneUdaje(id: number) {
    await UserDataService.getFakturacneUdajeById(id)
        .then((response: ResponseData) => {
            currentFakturacneUdaje.value = response.data.data
            if (currentFakturacneUdaje.value.address_id > 0) {
                getOrderAddress(currentFakturacneUdaje.value.address_id)
            }
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanySubStatuses() {
    CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
            companySubStatuses.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getHeadquartersTypes() {
    HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
            headquartersTypes.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getSposobyKonaniaKonatelov() {
    CompanyDataService.getSposobyKonaniaKonatelov()
        .then((response: ResponseData) => {
            sposobyKonaniaKonatelov.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getKonecnyUzivateliaVyhod() {
    CompanyDataService.getKonecnyUzivateliaVyhod()
        .then((response: ResponseData) => {
            konecnyUzivateliaVyhod.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function updateSubStatus() {
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            toast.success('Stav žiadosti bol úspešne zmenený');
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

async function refreshData() {
    items.value = JSON.parse(currentOrder.value.items)
    await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
    await getUser(currentOrder.value.user_id)
    await getCompany(currentOrder.value.company_id)
    await getCompanySubStatuses();
    await getHeadquartersTypes();
    await getSposobyKonaniaKonatelov();
    await getKonecnyUzivateliaVyhod();

    isLoaded.value = true;
}

onMounted(async () => {
    await refreshData();
})
</script>

<template>
    <div v-if="isLoaded">
        <div class="max-w-(--breakpoint-2xl) mx-auto flex-col gap-8">
            <!-- Header Card -->
            <Card class="flex-col gap-4">
                <CardHeader class="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-200">
                    <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 24">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    Stav žiadosti
                </CardHeader>
                <CardContent class="flex-col gap-4">
                    <div class="relative">
                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-brand-400 pointer-events-none"
                            fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                            <path d="M7 10l5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                        <FormKit type="select" v-model="currentCompany.sub_status" name="sub_status" id="sub_status"
                            @input="updateSubStatus()"
                            :classes="{ outer: 'relative', input: 'mt-1 block w-full appearance-none rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 py-2.5 pl-10 pr-4 text-sm text-gray-800 dark:text-gray-200 shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:bg-gray-900 dark:text-white/90' }">
                            <option>Vyberte stav žiadosti</option>
                            <option v-for="status in companySubStatuses" :key="status.id" :value="status.id">{{ status.description }}</option>
                        </FormKit>
                    </div>
                    <div class="flex-wrap gap-4 items-center mt-2">
                        <span class="inline-flex items-center gap-2 text-base font-semibold text-gray-800 dark:text-gray-200">
                            <svg v-if="currentCompany.type == 2" class="size-5 text-brand-500" fill="none"
                                stroke="currentColor" stroke-width="2" viewBox="0 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                                <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <svg v-else-if="currentCompany.type == 3" class="size-5 text-blue-500" fill="none"
                                stroke="currentColor" stroke-width="2" viewBox="0 24">
                                <circle cx="9" cy="9" r="2" stroke="currentColor" stroke-width="2" fill="none" />
                                <path d="M9 11v5M12 13h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <svg v-else class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                                viewBox="0 24">
                                <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                                    fill="none" />
                                <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                            </svg>
                        </span>
                        <span class="inline-flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-200">
                            <svg class="size-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2"
                                viewBox="0 24">
                                <path d="M16 7a4 4 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                            </svg>
                            {{ currentCompany.name }}
                        </span>
                    </div>
                    <div v-if="currentCompany.note" class="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/5/5 rounded-sm p-2 mt-2">
                        <b>Poznámky:</b> {{ currentCompany.note }}
                    </div>
                </CardContent>
            </Card>

            <!-- Tabs with icons -->
            <div class="flex-wrap gap-2 justify-center">
                <Button :variant="tab === 1 ? 'default' : 'ghost'" size="sm" @click="currentTab(1)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <path d="M3 7h18M3 12h18M3 17h18" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" />
                        </svg>
                        Objednané služby
                    </span>
                </Button>
                <Button :variant="tab === 2 ? 'default' : 'ghost'" size="sm" @click="currentTab(2)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <path d="M12 20V10M12 10L8 14M12 10l4 4" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        Predmety podnikania
                    </span>
                </Button>
                <Button :variant="tab === 3 ? 'default' : 'ghost'" size="sm" @click="currentTab(3)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                                fill="none" />
                            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        Obchodné sídlo
                    </span>
                </Button>
                <Button :variant="tab === 4 ? 'default' : 'ghost'" size="sm" @click="currentTab(4)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <path d="M16 12a4 4 0 1-8 0 4 0 1 8 0z" stroke="currentColor" stroke-width="2"
                                fill="none" />
                            <path d="M12 16v2m-4 2h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        Údaje o spoločnosti
                    </span>
                </Button>
                <Button :variant="tab === 5 ? 'default' : 'ghost'" size="sm" @click="currentTab(5)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        Podnikateľské údaje
                    </span>
                </Button>
                <Button :variant="tab === 7 ? 'default' : 'ghost'" size="sm" @click="currentTab(7)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-blue-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <circle cx="9" cy="9" r="2" stroke="currentColor" stroke-width="2" fill="none" />
                            <path d="M9 11v5M12 13h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        Osobné údaje
                    </span>
                </Button>
                <Button :variant="tab === 6 ? 'default' : 'ghost'" size="sm" @click="currentTab(6)">
                    <span class="inline-flex items-center gap-1">
                        <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24">
                            <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                                fill="none" />
                            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        Fakturačné údaje
                    </span>
                </Button>
            </div>

            <Card class="mt-6 flex-col gap-8 p-3">
                <!--PREDMETY PODNIKANIA-->
                <div v-if="tab === 1">
                    <div class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-gray-50 dark:bg-white/5/5 border-gray-100 dark:border-gray-800">
                        <svg class="size-6 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 24" title="Súčet cien">
                            <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                                fill="none" />
                            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        <span class="font-semibold text-gray-900 dark:text-gray-200">Suma všetkých služieb:</span>
                        <span class="ml-auto text-xl font-bold text-brand-700">{{items.reduce((sum, i) => sum +
                            (isNaN(parseFloat(i.price ?? '0')) ? 0 : parseFloat(i.price ?? '0')), 0).toFixed(2)}}
                            €</span>
                    </div>
                    <Card class="overflow-hidden">
                        <CardContent class="flex-col gap-4">
                            <div v-for="(item, index) in items" :key="index"
                                class="transition hover:bg-brand-50 dark:hover:bg-white/5 dark:hover:bg-brand-500/15 dark:hover:bg-white/5/5 rounded-lg border-gray-100 dark:border-gray-800 p-3 group">
                                <div class="grid-cols-9 gap-6 items-center">
                                    <div class="col-span-1 text-center self-center text-gray-400 dark:text-gray-500 font-semibold">#{{
                                        index + 1 }}</div>
                                    <div class="col-span-3 sm:col-span-3">
                                        <label for="description"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
                                        <div class="flex items-center gap-2">
                                            <svg class="size-4 text-brand-400" fill="none" stroke="currentColor"
                                                stroke-width="2" viewBox="0 24" title="Popis služby">
                                                <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor"
                                                    stroke-width="2" fill="none" />
                                                <path d="M8 12h8M8 12h8" stroke="currentColor" stroke-width="2"
                                                    stroke-linecap="round" />
                                            </svg>
                                            <span>{{ item.description }}</span>
                                        </div>
                                    </div>
                                    <div class="col-span-3 sm:col-span-3">
                                        <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Cena</label>
                                        <Input v-model="item.price" name="amount" id="amount"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md group-hover:border-brand-400 transition" />
                                    </div>
                                    <div class="col-span-2 sm:col-span-2">
                                        <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">DPH</label>
                                        <Input v-model="item.price_vat" name="amount" id="amount"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md group-hover:border-brand-400 transition" />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <!--PREDMETY PODNIKANIA-->
                <div v-if="tab === 2">
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <Card class="overflow-hidden">
                            <CardContent class="flex-col gap-4">
                                <div class="grid-cols-9 gap-6" v-for="(item, index) in predmetyPodnikania"
                                    :key="index">
                                        <div class="col-span-1 text-center self-center">
                                            {{ index }}
                                        </div>
                                        <div class="col-span-3 sm:col-span-3">
                                            <label for="description"
                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
                                            <div>{{ item.title }}</div>
                                        </div>

                                        <div class="col-span-3 sm:col-span-3">
                                            <label for="amount"
                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Cena</label>
                                            <Input v-model="item.price" name="amount" id="amount"
                                                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                        </div>
                                    </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <!--OBCHODNE SIDLO-->
                <div v-if="tab === 3">
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <Card class="overflow-hidden">
                            <CardContent class="flex-col gap-4">
                                <div class="flex-row pb-4">
                                    <div class="flex-col w-full">
                                        <label for="is_virtual"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Virtuálne sídlo?</label>
                                        <FormKit type="checkbox" disabled v-model="currentHeadquarters.is_virtual" name="is_virtual" id="is_virtual"
                                            :classes="{ input: 'size-5 rounded border-gray-300 dark:border-gray-700 text-brand-500 focus:ring-brand-500  dark:bg-gray-900' }" />
                                    </div>
                                </div>
                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col w-full">
                                        <label for="country"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krajina</label>
                                        <Input v-model="currentCompanyAddress.country" name="country"
                                            id="country"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                        <Input v-model="currentCompanyAddress.city" name="city" id="city"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="psc" class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                        <Input v-model="currentCompanyAddress.psc" name="psc" id="psc"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col w-full">
                                        <label for="street"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                        <Input v-model="currentCompanyAddress.street" name="street"
                                            id="street"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="street_number"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Súpisné
                                            číslo</label>
                                        <Input v-model="currentCompanyAddress.street_number"
                                            name="street_number" id="street_number"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="street_number2"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                            číslo</label>
                                        <Input v-model="currentCompanyAddress.street_number2"
                                            name="street_number2" id="street_number2"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                                <div class="flex-row gap-4">
                                    <div class="flex-col w-full">
                                        <label for="druh_priestoru" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Druh
                                            priestoru</label>
                                        <FormKit type="select" v-model="currentHeadquarters.headquarters_type" name="druh_priestoru" id="druh_priestoru"
                                            :classes="{ input: 'mt-1 block w-full appearance-none rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 py-2.5 ps-4 pe-4 text-sm text-gray-800 dark:text-gray-200 shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20  dark:bg-gray-900 dark:text-white/90' }">
                                            <option class="text-gray-500 dark:text-gray-400 dark:text-gray-500">Vyberte druh priestoru</option>
                                            <option v-for="hqType in headquartersTypes" :key="hqType.id"
                                                :value="hqType.id">{{ hqType.description }}</option>
                                        </FormKit>
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="description"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Vlastník</label>
                                        <Input v-model="currentHeadquarters.owner_name" name="description"
                                            id="description"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <!--UDAJE O SPOLONOCSTI-->
                <div v-if="tab === 4">
                    <div class="flex-col gap-6">

                        <!-- Názov spoločnosti -->
                        <Card class="overflow-hidden">
                            <CardHeader class="flex-row items-center gap-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/5/5 px-5 py-3">
                                <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2" fill="none"/>
                                    <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                <span class="font-semibold text-gray-800 dark:text-gray-200 text-sm">Názov spoločnosti</span>
                            </CardHeader>
                            <CardContent>
                                <div class="flex-col">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Názov</label>
                                    <Input v-model="currentCompany.name" name="company_name"
                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Základné imanie -->
                        <Card class="overflow-hidden">
                            <CardHeader class="flex-row items-center gap-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/5/5 px-5 py-3">
                                <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                                    <path d="M12 8c-2.2 0-4 1.3-4 3s1.8 3 4 3 4 1.3 4 3-1.8 3-4 3M12 8v1m0 9v1m0-10c1.7 0 3.2.7 3.8 1.8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                <span class="font-semibold text-gray-800 dark:text-gray-200 text-sm">Základné imanie</span>
                            </CardHeader>
                            <CardContent>
                                <div class="flex-col">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Výška €</label>
                                    <Input number v-model="currentCompany.imanie_vyska" name="imanie_vyska"
                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                </div>
                                <div class="flex-col">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rozsah splatenia €</label>
                                    <Input number v-model="currentCompany.imanie_splatene" name="imanie_splatene"
                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Zakladatelia -->
                        <Card class="overflow-hidden">
                            <CardHeader class="flex-row items-center gap-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/5/5 px-5 py-3">
                                <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                                    <circle cx="9" cy="7" r="3" stroke="currentColor" stroke-width="2" fill="none"/>
                                    <path d="M3 20c0-3.3 2.7-6-6h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                    <circle cx="17" cy="14" r="3" stroke="currentColor" stroke-width="2" fill="none"/>
                                    <path d="M14 20h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                <span class="font-semibold text-gray-800 dark:text-gray-200 text-sm">Zakladatelia</span>
                            </CardHeader>
                            <CardContent>
                                <template v-for="(companyMember, index) in currentCompanyMembers" :key="'zak-' + index">
                                    <Card v-show="companyMember.je_zakladatel" class="overflow-hidden p-0 sm:p-0">
                                        <!-- Member card header -->
                                        <CardHeader class="flex-row items-center justify-between gap-2 border-b border-brand-100 dark:border-brand-500/25 bg-brand-50 dark:bg-brand-500/15 px-4 py-2">
                                            <span class="text-sm font-semibold text-brand-800 dark:text-brand-300">Zakladateľ #{{ index + 1 }}</span>
                                            <Badge size="sm" :color="companyMember.typ_zakladatela == 2 ? 'info' : 'primary'">
                                                {{ companyMember.typ_zakladatela == 2 ? 'Právnická osoba' : 'Fyzická osoba' }}
                                            </Badge>
                                        </CardHeader>
                                        <CardContent class="flex-col gap-4 p-4">
                                            <!-- Typ -->
                                            <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Typ zakladateľa</label>
                                                    <FormKit type="select" v-model="companyMember.typ_zakladatela" name="typ_zakladatela"
                                                        :classes="{ input: 'mt-1 block w-full appearance-none rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 py-2.5 ps-4 pe-4 text-sm text-gray-800 dark:text-gray-200 shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20  dark:bg-gray-900 dark:text-white/90' }">
                                                        <option :value="1">Fyzická osoba</option>
                                                        <option :value="2">Právnická osoba</option>
                                                    </FormKit>
                                                </div>
                                                <!-- Fyzická osoba: meno, priezvisko, dátum nar -->
                                                <div class="flex-col" v-if="companyMember.typ_zakladatela == 1">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Krstné meno</label>
                                                    <Input v-model="companyMember.first_name" name="first_name"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col" v-if="companyMember.typ_zakladatela == 1">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priezvisko</label>
                                                    <Input v-model="companyMember.last_name" name="last_name"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <!-- Právnická osoba: obchodné meno, IČO -->
                                                <div class="flex-col" v-if="companyMember.typ_zakladatela == 2">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Obchodné meno</label>
                                                    <Input v-model="companyMember.obchodne_meno" name="obchodne_meno"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col" v-if="companyMember.typ_zakladatela == 2">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IČO</label>
                                                    <Input v-model="companyMember.ico" name="ico"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                            </div>

                                            <!-- Fyzická osoba: ďalšie osobné údaje -->
                                            <template v-if="companyMember.typ_zakladatela == 1">
                                                <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                    <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Osobné údaje</p>
                                                    <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dátum narodenia</label>
                                                            <Input type="date" v-model="companyMember.date_of_birth" name="date_of_birth"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pohlavie</label>
                                                            <Input v-model="companyMember.gender" name="gender"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Národnosť</label>
                                                            <Input v-model="companyMember.nationality" name="nationality"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titul pred menom</label>
                                                            <Input v-model="companyMember.title_before" name="title_before"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titul za menom</label>
                                                            <Input v-model="companyMember.title_after" name="title_after"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rodné číslo</label>
                                                            <Input v-model="companyMember.rodne_cislo" name="rodne_cislo"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Typ dokladu totožnosti</label>
                                                            <Input v-model="companyMember.typ_dokladu_totoznosti" name="typ_dokladu_totoznosti"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                        <div class="flex-col">
                                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Číslo dokladu totožnosti</label>
                                                            <Input v-model="companyMember.cislo_dokladu_totoznosti" name="cislo_dokladu_totoznosti"
                                                                class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>

                                            <!-- Adresa -->
                                            <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Adresa trvalého pobytu</p>
                                                <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ulica</label>
                                                        <Input v-model="companyMember.street" name="street"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Súpisné číslo</label>
                                                        <Input v-model="companyMember.street_number" name="street_number"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orientačné číslo</label>
                                                        <Input v-model="companyMember.street_number2" name="street_number2"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mesto</label>
                                                        <Input v-model="companyMember.city" name="city"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PSČ</label>
                                                        <Input v-model="companyMember.psc" name="psc"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Krajina</label>
                                                        <Input v-model="companyMember.country" name="country"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Vklad a podiel -->
                                            <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Vklad a podiel</p>
                                                <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Výška vkladu €</label>
                                                        <Input number v-model="companyMember.vyska_vkladu" name="vyska_vkladu"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Podiel v spoločnosti %</label>
                                                        <Input number v-model="companyMember.podiel_v_spolocnosti" name="podiel_v_spolocnosti"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rozsah splatenia vkladu €</label>
                                                        <Input number v-model="companyMember.rozsah_splatenia_vkladu" name="rozsah_splatenia_vkladu"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Checkboxes -->
                                                <div class="pt-2 border-t border-gray-100 dark:border-gray-800 flex-col gap-2">
                                                    <label class="flex items-center gap-2 cursor-default text-sm text-gray-700 dark:text-gray-300">
                                                        <FormKit type="checkbox" disabled v-model="companyMember.je_konatel" name="je_konatel"
                                                            :classes="{ input: 'size-4 rounded-sm border-gray-300 dark:border-gray-700 text-brand-500 focus:ring-brand-500  dark:bg-gray-900' }" />
                                                        Tento zakladateľ (spoločník) bude aj konateľom
                                                    </label>
                                                    <label class="flex items-center gap-2 cursor-default text-sm text-gray-700 dark:text-gray-300">
                                                        <FormKit type="checkbox" disabled v-model="companyMember.je_spravca_vkladu" name="je_spravca_vkladu"
                                                            :classes="{ input: 'size-4 rounded-sm border-gray-300 dark:border-gray-700 text-brand-500 focus:ring-brand-500  dark:bg-gray-900' }" />
                                                        Tento zakladateľ (spoločník) bude aj správcom vkladu
                                                    </label>
                                                </div>

                                            <!-- Zastupiteľ (právnická osoba) -->
                                            <div v-if="companyMember.typ_zakladatela == 2" class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Zastupiteľ</p>
                                                <div class="grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meno</label>
                                                        <Input v-model="companyMember.zastupitel.first_name" name="zastupitel_first_name"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priezvisko</label>
                                                        <Input v-model="companyMember.zastupitel.last_name" name="zastupitel_last_name"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rodné číslo</label>
                                                        <Input v-model="companyMember.zastupitel.rodne_cislo" name="zastupitel_rodne_cislo"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dátum narodenia</label>
                                                        <Input v-model="companyMember.zastupitel.date_of_birth" name="zastupitel_dob"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </template>
                            </CardContent>
                        </Card>

                        <!-- Konatelia -->
                        <Card class="overflow-hidden">
                            <CardHeader class="flex-row items-center gap-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/5/5 px-5 py-3">
                                <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                                    <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2" fill="none"/>
                                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                <span class="font-semibold text-gray-800 dark:text-gray-200 text-sm">Konatelia</span>
                            </CardHeader>
                            <CardContent>
                                <template v-for="(companyMember, index) in currentCompanyMembers" :key="'kon-' + index">
                                    <Card v-show="companyMember.je_konatel" class="overflow-hidden p-0 sm:p-0">
                                        <CardHeader class="flex-row items-center gap-2 border-b border-blue-100 dark:border-blue-500/25 bg-blue-50 dark:bg-blue-500/15 px-4 py-2">
                                            <span class="text-sm font-semibold text-blue-800 dark:text-blue-300">Konateľ #{{ index + 1 }}</span>
                                        </CardHeader>
                                        <CardContent class="flex-col gap-4 p-4">
                                            <!-- Základné info -->
                                            <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Krstné meno</label>
                                                    <Input v-model="companyMember.first_name" name="first_name"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priezvisko</label>
                                                    <Input v-model="companyMember.last_name" name="last_name"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dátum narodenia</label>
                                                    <Input type="date" v-model="companyMember.date_of_birth" name="date_of_birth"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pohlavie</label>
                                                    <Input v-model="companyMember.gender" name="gender"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Národnosť</label>
                                                    <Input v-model="companyMember.nationality" name="nationality"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                                <div class="flex-col">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rodné číslo</label>
                                                    <Input v-model="companyMember.rodne_cislo" name="rodne_cislo"
                                                        class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                            </div>

                                            <!-- Tituly a doklady -->
                                            <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Tituly a doklad totožnosti</p>
                                                <div class="grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titul pred menom</label>
                                                        <Input v-model="companyMember.title_before" name="title_before"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titul za menom</label>
                                                        <Input v-model="companyMember.title_after" name="title_after"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Typ dokladu totožnosti</label>
                                                        <Input v-model="companyMember.typ_dokladu_totoznosti" name="typ_dokladu_totoznosti"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Číslo dokladu totožnosti</label>
                                                        <Input v-model="companyMember.cislo_dokladu_totoznosti" name="cislo_dokladu_totoznosti"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Adresa -->
                                            <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                                                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Adresa trvalého pobytu</p>
                                                <div class="grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ulica</label>
                                                        <Input v-model="companyMember.street" name="street"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Súpisné číslo</label>
                                                        <Input v-model="companyMember.street_number" name="street_number"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orientačné číslo</label>
                                                        <Input v-model="companyMember.street_number2" name="street_number2"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mesto</label>
                                                        <Input v-model="companyMember.city" name="city"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PSČ</label>
                                                        <Input v-model="companyMember.psc" name="psc"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                    <div class="flex-col">
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Krajina</label>
                                                        <Input v-model="companyMember.country" name="country"
                                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </template>
                            </CardContent>
                        </Card>

                        <!-- KUV a Spôsob konania -->
                        <Card class="overflow-hidden">
                            <CardHeader class="flex-row items-center gap-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/5/5 px-5 py-3">
                                <svg class="size-4 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 24">
                                    <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2" fill="none"/>
                                </svg>
                                <span class="font-semibold text-gray-800 dark:text-gray-200 text-sm">Konečný užívateľ výhod a spôsob konania</span>
                            </CardHeader>
                            <CardContent>
                                <div class="flex-col gap-3">
                                    <div class="flex-col">
                                        <label for="konecny_uzivatelia" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Konečným užívateľom výhod sú</label>
                                        <FormKit type="select" v-model="currentCompany.konecny_uzivatelia_vyhod"
                                            name="konecny_uzivatelia" id="konecny_uzivatelia"
                                            :classes="{ input: 'mt-1 block w-full appearance-none rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 py-2.5 ps-4 pe-4 text-sm text-gray-800 dark:text-gray-200 shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20  dark:bg-gray-900 dark:text-white/90' }">
                                            <option class="text-gray-500 dark:text-gray-400 dark:text-gray-500">Vyberte druh konečného užívania výhod</option>
                                            <option v-for="kuv in konecnyUzivateliaVyhod" :key="kuv.id" :value="kuv.id">{{ kuv.description }}</option>
                                        </FormKit>
                                    </div>
                                    <div class="flex-col">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Detaily</label>
                                        <Input v-model="currentCompany.kuv_details" name="kuv_details"
                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                                <div class="flex-col gap-3">
                                    <div class="flex-col">
                                        <label for="sposob_konania" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spôsob konania konateľov</label>
                                        <FormKit type="select" v-model="currentCompany.sposob_konania_konatelov"
                                            name="sposob_konania" id="sposob_konania"
                                            :classes="{ input: 'mt-1 block w-full appearance-none rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 py-2.5 ps-4 pe-4 text-sm text-gray-800 dark:text-gray-200 shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20  dark:bg-gray-900 dark:text-white/90' }">
                                            <option class="text-gray-500 dark:text-gray-400 dark:text-gray-500">Vyberte druh spôsobu konania konateľov</option>
                                            <option v-for="skk in sposobyKonaniaKonatelov" :key="skk.id" :value="skk.id">{{ skk.description }}</option>
                                        </FormKit>
                                    </div>
                                    <div class="flex-col">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Detaily</label>
                                        <Input v-model="currentCompany.skk_details" name="skk_details"
                                            class="focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                    </div>
                </div>
                <!--PODNIKATELSKE UDAJE-->
                <div v-if="tab === 5">
                    <div class="mt-5">
                        <Card class="overflow-hidden">
                            <CardContent class="flex-col gap-4">
                                <div class="flex-row pb-4">
                                    <div class="flex-col w-full">
                                        <label for="company_id" class="text-xl font-bold">Názov spoločnosti</label>
                                        <Input v-model="currentCompany.name" name="first_name"
                                            id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col">
                                        <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krstné
                                            meno</label>
                                        <Input v-model="currentCompanyMembers[0].first_name"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col">
                                        <label for="first_name"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Priezvisko</label>
                                        <Input v-model="currentCompanyMembers[0].last_name"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col">
                                        <label for="first_name"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Pohlavie</label>
                                        <Input v-model="currentCompanyMembers[0].gender" name="first_name"
                                            id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>

                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col">
                                        <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul
                                            pred
                                            menom</label>
                                        <Input v-model="currentCompanyMembers[0].title_before"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col">
                                        <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul za
                                            menom</label>
                                        <Input v-model="currentCompanyMembers[0].title_after"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>

                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col w-full">
                                        <label for="first_name"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Telefonné
                                            číslo</label>
                                        <Input v-model="currentUser.phone" name="first_name"
                                            id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="dob2" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum
                                            narodenia</label>
                                        <Input type="date" v-model="currentCompanyMembers[0].date_of_birth" name="dob"
                                            id="dob2"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rodné
                                            číslo</label>
                                        <Input v-model="currentCompanyMembers[0].rodne_cislo"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="first_name"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Národnosť</label>
                                        <Input v-model="currentCompanyMembers[0].nationality"
                                            name="first_name" id="first_name"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>

                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col w-full">
                                        <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                        <Input v-model="currentCompanyMembers[0].city" name="city"
                                            id="city"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="street"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                        <Input v-model="currentCompanyMembers[0].street" name="street"
                                            id="street"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="street_number"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Súpisné
                                            číslo</label>
                                        <Input v-model="currentCompanyMembers[0].street_number"
                                            name="street_number" id="street_number"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="street_number2"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                            číslo</label>
                                        <Input v-model="currentCompanyMembers[0].street_number2"
                                            name="street_number2" id="street_number2"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="psc" class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                        <Input v-model="currentCompanyMembers[0].psc" name="psc" id="psc"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>

                                    <div class="flex-col w-full">
                                        <label for="country"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krajina</label>
                                        <Input v-model="currentCompanyMembers[0].country" name="country"
                                            id="country"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>

                                <div class="flex-row gap-4">
                                    <div class="flex-col w-full">
                                        <label for="registracia"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Registrácia
                                            živnosti ku dňu?</label>
                                        <Input v-model="currentCompany.zaciatok_opravnenia"
                                            name="registracia" id="registracia"
                                            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <!--FAKTURACNE UDAJE-->
                <div v-if="tab === 6">
                    <div class="px-4 py-6 sm:px-0">
                        <div class="mt-10 sm:mt-0">
                            <div class="md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <div class="px-4 sm:px-0">
                                        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-gray-200">Fakturačné údaje</h3>
                                        <p>Vypĺňajú sa údaje buď na IČO alebo klienta, nikdy nie oboje.</p>
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <Card class="overflow-hidden">
                                        <CardContent class="flex-col gap-4">
                                            <div class="grid-cols-6 gap-6">
                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="first_name"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krstné
                                                        meno</label>
                                                    <Input v-model="currentFakturacneUdaje.first_name"
                                                        name="first_name" id="first_name"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="last_name"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Priezvisko</label>
                                                    <Input v-model="currentFakturacneUdaje.last_name"
                                                        name="last_name" id="last_name"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="name"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Názov
                                                        spoločnosti</label>
                                                    <Input v-model="currentFakturacneUdaje.name"
                                                        name="name" id="name"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="ico"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČO</label>
                                                    <Input v-model="currentFakturacneUdaje.ico" name="ico"
                                                        id="ico"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="dic"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">DIČ</label>
                                                    <Input v-model="currentFakturacneUdaje.dic" name="dic"
                                                        id="dic"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="ic_dph"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČ
                                                        DPH</label>
                                                    <Input v-model="currentFakturacneUdaje.ic_dph"
                                                        name="ic_dph" id="ic_dph"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="px-4 py-6 sm:px-0">
                        <div class="mt-10 sm:mt-0">
                            <div class="md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <div class="px-4 sm:px-0">
                                        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-gray-200">Adresa</h3>
                                        <p>Adresa pre Fakturacne udaje</p>
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <Card class="overflow-hidden">
                                        <CardContent class="flex-col gap-4">
                                            <div class="grid-cols-6 gap-6">
                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="city"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                                    <Input v-model="currentOrderAddress.city" name="city"
                                                        id="city"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="street"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                                    <Input v-model="currentOrderAddress.street"
                                                        name="street" id="street"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="psc"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                                    <Input v-model="currentOrderAddress.psc" name="psc"
                                                        id="psc"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>

                                                <div class="col-span-6 sm:col-span-3">
                                                    <label for="country"
                                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krajina</label>
                                                    <Input v-model="currentOrderAddress.country"
                                                        name="country" id="country"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tab 7: Type 3 (Fyzos) Personal Details -->
                <div v-if="tab === 7">
                    <div class="mt-5">
                        <Card class="overflow-hidden">
                            <CardContent class="flex-col gap-4">
                                <div class="flex-row pb-4 gap-4">
                                    <div class="flex-col w-full">
                                        <label for="company_name_fyzos"
                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Meno a priezvisko</label>
                                        <Input v-model="currentCompany.name" name="company_name_fyzos"
                                            id="company_name_fyzos"
                                            class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">Názov spoločnosti je tvorený z mena a
                                            priezviska</p>
                                    </div>
                                    <div class="flex-col w-full">
                                        <label for="ico_fyzos" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rodné
                                            číslo</label>
                                        <Input v-model="currentCompany.ico" name="ico_fyzos"
                                            id="ico_fyzos"
                                            class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-md" />
                                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">IČO firmy = Rodné číslo</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <CardFooter class="justify-end bg-gray-50 dark:bg-white/5/5 px-4 py-3">
                    <Button type="submit">Uložiť</Button>
                </CardFooter>
            </Card>
        </div>
    </div>
</template>